/**
 * OCR Real-time Overlay Component
 * Displays live OCR extraction data as it's processed
 * Shows vendor details and items progressively with animated cards
 */

import React, { useEffect, useState } from 'react';
import '../styles/OCRRealtimeOverlay.css';

const OCRRealtimeOverlay = ({ 
  show, 
  vendorData, 
  items, 
  stage, 
  progress, 
  onCancel,
  stageName 
}) => {
  const [animatedItems, setAnimatedItems] = useState([]);

  // Animate items as they come in
  useEffect(() => {
    if (items && items.length > animatedItems.length) {
      const newItems = items.slice(animatedItems.length);
      newItems.forEach((item, idx) => {
        setTimeout(() => {
          setAnimatedItems(prev => [...prev, item]);
        }, idx * 150); // Stagger animation by 150ms
      });
    }
  }, [items]);

  // Reset when overlay closes
  useEffect(() => {
    if (!show) {
      setAnimatedItems([]);
    }
  }, [show]);

  if (!show) return null;

  return (
    <div className="ocr-realtime-overlay">
      <div className="ocr-overlay-backdrop" onClick={onCancel} />
      
      <div className="ocr-overlay-content">
        {/* Header */}
        <div className="ocr-overlay-header">
          <div className="ocr-status-badge">
            <div className="ocr-pulse-dot" />
            <span>Live OCR Processing</span>
          </div>
          <button className="ocr-cancel-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="ocr-progress-section">
          <div className="ocr-progress-bar-container">
            <div 
              className="ocr-progress-bar-fill" 
              style={{ width: `${progress || 0}%` }}
            />
          </div>
          <div className="ocr-progress-info">
            <span className="ocr-stage-text">
              📡 Stage {stage}/3: {stageName || 'Processing'}
            </span>
            <span className="ocr-progress-percent">{Math.round(progress || 0)}%</span>
          </div>
        </div>

        {/* Content Area */}
        <div className="ocr-data-container">
          {/* Vendor Card */}
          {vendorData && (
            <div className="ocr-vendor-card animate-fade-in">
              <div className="ocr-card-header">
                <div className="ocr-card-icon">🏪</div>
                <h3>Vendor Details</h3>
              </div>
              <div className="ocr-card-body">
                <div className="ocr-data-row">
                  <span className="ocr-label">Name:</span>
                  <span className="ocr-value">{vendorData.vendor_name}</span>
                </div>
                {vendorData.vendor_phone && (
                  <div className="ocr-data-row">
                    <span className="ocr-label">Phone:</span>
                    <span className="ocr-value">{vendorData.vendor_phone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Items Grid */}
          {animatedItems.length > 0 && (
            <div className="ocr-items-section">
              <div className="ocr-section-header">
                <div className="ocr-card-icon">📦</div>
                <h3>Extracted Items ({animatedItems.length})</h3>
              </div>
              <div className="ocr-items-grid">
                {animatedItems.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="ocr-item-card animate-slide-in"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <div className="ocr-item-name">
                      {item.name || item.item_name || 'Unknown Item'}
                    </div>
                    <div className="ocr-item-details">
                      <span className="ocr-item-quantity">
                        {item.quantity} {item.unit || 'units'}
                      </span>
                      {item.price && (
                        <span className="ocr-item-price">
                          ₹{parseFloat(item.price).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {!vendorData && animatedItems.length === 0 && (
            <div className="ocr-loading-state">
              <div className="ocr-spinner" />
              <p className="ocr-loading-text">
                {stage === 1 && 'Extracting text from receipt...'}
                {stage === 1.5 && 'Translating text...'}
                {stage === 2 && 'Parsing with AI...'}
                {stage === 3 && 'Validating data...'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OCRRealtimeOverlay;
